import { MouseEvent } from 'react';
import { Box, FormControl } from '@chakra-ui/react';
import { Button, Text, Colors, Checkbox, Icon } from '@mercator-gcp/grout';
import { capitalize } from 'lodash';
import BasicModal from 'components/elements/BasicModal';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { STATE_CODE_TO_LABEL } from 'utils/constants/location';

const Separator = () => (
  <Box
    sx={{
      height: '1px',
      backgroundColor: Colors.ArmyGreen200,
      width: '100%',
    }}
  />
);

const emptyStateStyle = {
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: 'padding_16',
  paddingBottom: 'padding_16',
};

const formHeaderStyle = { paddingBottom: 'padding_16' };
const formBodyStyle = {
  width: '100%',
  maxHeight: '400px',
  overflowY: 'auto',
  minWidth: '300px',
};
const checkboxStyle = {
  paddingY: 'padding_16',
  borderRadius: '4px',
  '>span:last-child': {
    width: '100%',
  },
};
const checkboxLabelStyle = {
  marginLeft: 'padding_16',
  justifyContent: 'space-between',
  gap: 'padding_4',
};

const EmptyState = () => (
  <FlexColumn sx={emptyStateStyle}>
    <Text.LargeBold>{`You don't have any company groups yet!`}</Text.LargeBold>
    <Text>Create your first company group.</Text>
  </FlexColumn>
);

type GroupItem = { id: number; name: string; state_code: string };

type GroupSelectionViewProps = {
  displayGroups: GroupItem[] | undefined;
  isLoading: boolean;
  hasCompanyGroups: boolean;
  selectedItems: number[];
  selectedListsLabel: string;
  onToggleGroup: (groupId: number) => void;
  onCreateNewGroup: () => void;
  onClose: () => void;
  onConfirm: (event: MouseEvent<HTMLButtonElement>) => void;
  isConfirmLoading: boolean;
  isConfirmDisabled: boolean;
};

const GroupSelectionView = ({
  displayGroups,
  isLoading,
  hasCompanyGroups,
  selectedItems,
  selectedListsLabel,
  onToggleGroup,
  onCreateNewGroup,
  onClose,
  onConfirm,
  isConfirmLoading,
  isConfirmDisabled,
}: GroupSelectionViewProps) => {
  return (
    <>
      <BasicModal.Body title="Save to group">
        <FlexRow sx={formHeaderStyle}>
          <Text>{selectedListsLabel}</Text>
          <Button
            variant="secondary"
            onClick={onCreateNewGroup}
            startAdornment={<Icon name={Icon.Name.Plus} />}
          >
            New group
          </Button>
        </FlexRow>
        <FormControl>
          <FlexColumn sx={formBodyStyle}>
            {isLoading && <Spinner />}
            {!isLoading && !hasCompanyGroups && <EmptyState />}
            {hasCompanyGroups &&
              displayGroups?.map(({ id, name, state_code }, index) => (
                <FlexColumn key={id}>
                  {index === 0 && <Separator />}
                  <Checkbox
                    size="small"
                    sx={checkboxStyle}
                    value={id}
                    isChecked={selectedItems.includes(id)}
                    onToggle={() => onToggleGroup(id)}
                  >
                    <FlexRow sx={checkboxLabelStyle}>
                      <Text.BodyBold>{capitalize(name)}</Text.BodyBold>
                      <Text.Body>{STATE_CODE_TO_LABEL[state_code]}</Text.Body>
                    </FlexRow>
                  </Checkbox>
                  {displayGroups?.length !== index + 1 && <Separator />}
                  {displayGroups?.length === index + 1 && <Separator />}
                </FlexColumn>
              ))}
          </FlexColumn>
        </FormControl>
      </BasicModal.Body>
      <BasicModal.Footer>
        <Button variant="functional" onClick={onClose} isDisabled={isConfirmLoading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          type="submit"
          isDisabled={isConfirmDisabled}
          isLoading={isConfirmLoading}
        >
          Add to selected
        </Button>
      </BasicModal.Footer>
    </>
  );
};

export default GroupSelectionView;
